<template>
  <MasterDetail
    ref="masterDetail"
    :actionBarButtons="actionBarButtons"
    :canDelete="false"
    :canEdit="false"
    class="master-detail"
    :confirmDeleteMessage="confirmDeleteMessage"
    :contextOptions="contextMenu"
    :cols="cols"
    :customResource="customResource"
    disableActContextOnClick
    :hasExportCSV="false"
    :hasExportXLSX="false"
    :hasNewButton="false"
    :opts="opts"
    :selection.sync="selection"
    selectionCol
    serverPagination
  >
    <input
      ref="inputFile"
      accept=".xlsx"
      class="input-file"
      type="file"
      @change="onInputChange"
    />

    <FormModal
      :cols="fields"
      :opened.sync="taxModal.opened"
      title="Alterar taxa de juros"
      :value.sync="taxModal.data"
      @save="doSaveTax"
    />

    <FormModal
      :cols="responsiblesFields"
      :opened.sync="responsiblesModal.opened"
      :opts="opts"
      title="Definir responsáveis pelo acompanhamento"
      :value.sync="responsiblesModal.data"
      @close="closeResponsiblesModal"
      @save="doSaveResponsibles"
    />

    <Modal v-model="openedExport" title="Exportar consolidado" width="600">
      <p>O processamento do arquivo é feito de forma assíncrona pelo Teros.</p>
      <p>
        Informe o nome do arquivo e aguarde a notificação de que o mesmo já está
        disponível para download.
      </p>

      <Field v-model="filenameExport" :label="fieldExport" />

      <template #buttons>
        <v-spacer />

        <v-btn
          class="px-5 ml-3"
          color="secondary"
          depressed
          @click="openedExport = false"
        >
          Cancelar
        </v-btn>

        <v-btn
          class="px-5 ml-3"
          color="primary"
          dark
          depressed
          @click="doExport"
        >
          Exportar
        </v-btn>
      </template>
    </Modal>

    <v-menu
      v-model="importMenuOpen"
      :activator="importMenuActivator"
      :attach="importMenuAttach"
      bottom
      offset-y
    >
      <v-list>
        <v-list-item @click="doImport('business-plan')">
          Business Plan
        </v-list-item>
        <v-list-item @click="doImport('vpl-consolidado')">
          VPL e Outras Despesas
        </v-list-item>
      </v-list>
    </v-menu>
  </MasterDetail>
</template>

<script>
import FormModal from "@/components/form-modal.vue";
import MasterDetail from "@/components/master-detail.vue";
import Modal from "@/components/g-modal.vue";
import Field from "@/components/input-v.vue";
import { FieldTypeEnum } from "@/core/enums/field-types";
import { AnyAdmin } from "@/core/enums/user-types";
import { partition, toNumber } from "lodash";
import { mapGetters } from "vuex";
import { TiposResponsaveisEnum } from "../responsaveis/responsaveis.types";
import { BusinessPlanStatus } from "./businessplan.types";
import {
  adjustmentAccountingStep,
  adjustmentStep
} from "../config/workflow.config";
import { userHasAccess } from "@/helpers/permissionValidator";

export default {
  components: {
    FormModal,
    MasterDetail,
    Modal,
    Field
  },
  data() {
    return {
      importMenuAttach: null,
      importMenuActivator: null,
      importMenuOpen: false,
      importType: "business-plan",
      opts: {
        businessPlans: [],
        responsaveis: [],
        statusOpts: [],
        workflows: []
      },
      responsiblesModal: {
        data: {},
        opened: false
      },
      selection: [],
      taxModal: {
        data: {},
        opened: false
      },
      openedExport: false,
      filenameExport: ""
    };
  },
  computed: {
    ...mapGetters(["clientId", "user"]),
    actionBarButtons() {
      const isAdmin = AnyAdmin.includes(this.user.tipo_usuario);

      const buttons = [
        {
          text: "Adicionar",
          icon: "mdi-plus-box-outline",
          action: () => {
            this.$router.push({ name: "ativacaoped-projetos-form" });
          }
        }
      ];

      if (this.hasAdminRole || isAdmin) {
        buttons.push(
          {
            text: "Alterar taxa de retorno",
            icon: "mdi-percent-box-outline",
            action: async () => {
              const taxa_juros_anual = await this.doGetTax();
              this.taxModal.data = { taxa_juros_anual };
              this.taxModal.opened = true;
            }
          },
          {
            text: "Definir responsáveis pelo acompanhamento",
            icon: "mdi-account-box-outline",
            hidden: this.selection.length === 0,
            action: async () => {
              this.openResponsibleModal();
            }
          },
          {
            text: "Importações",
            icon: "mdi-upload-box-outline",
            align: "right",
            action: pointerEvent => {
              this.importMenuActivator = null;
              this.importMenuAttach = null;

              this.$nextTick(() => {
                this.importMenuAttach = pointerEvent.currentTarget.parentElement;
                this.importMenuActivator = pointerEvent.currentTarget;

                this.$nextTick(() => {
                  this.importMenuOpen = true;
                });
              });
            }
          }
        );
      }

      if (
        this.hasAdminRole ||
        isAdmin ||
        userHasAccess(this.user, "AtivacaoPed.fpa-despesas.arquivos-oficiais")
      ) {
        buttons.push({
          text: "Exportar consolidados",
          icon: "mdi-download-box-outline",
          align: "right",
          action: async () => {
            this.filenameExport = "";
            this.openedExport = true;
          }
        });
      }

      return buttons.filter(({ hidden }) => !hidden);
    },
    contextMenu() {
      const contextOptions = [
        {
          name: "Alocação",
          show: this.selection.length <= 1,
          cb: ({ id }) => {
            this.$router.push({
              name: "ativacaoped-projetos-form-edicao",
              params: { id },
              query: { tab: "alocacao" }
            });
          }
        },
        {
          name: "Timesheet",
          show: this.selection.length <= 1,
          cb: ({ id }) => {
            this.$router.push({
              name: "ativacaoped-timesheet",
              params: { id }
            });
          }
        },
        {
          name: "Definir responsáveis pelo acompanhamento",
          show: true,
          cb: row => {
            this.openResponsibleModal(row);
          }
        },
        {
          name: "sep",
          show: this.selection.length <= 1
        },
        {
          name: "Editar",
          show: this.selection.length <= 1,
          cb: ({ id }) => {
            this.$router.push({
              name: "ativacaoped-projetos-form-edicao",
              params: { id }
            });
          }
        },
        {
          name: "Excluir",
          show: this.selection.length <= 1,
          cb: row => {
            if (this.$refs.masterDetail) {
              this.$refs.masterDetail.selectedItem = row;
              this.$refs.masterDetail.deleteDialog = true;
            }
          }
        }
      ];

      return contextOptions.filter(({ show }) => show);
    },
    cols() {
      return [
        {
          key: "nome",
          name: "Título do BP",
          type: this.$fieldTypes.TEXT,
          filterable: true,
          tooltip: true,
          width: "300px"
        },
        {
          key: "data_inicio",
          name: "Data de início",
          type: this.$fieldTypes.DATE,
          width: "200px"
        },
        {
          key: "data_fim",
          name: "Data de término",
          type: this.$fieldTypes.DATE,
          width: "200px"
        },
        {
          key: "status",
          name: "Status",
          type: this.$fieldTypes.SELECT,
          rel: { to: "statusOpts", key: "id", name: "label" },
          filterable: true,
          width: "200px"
        },
        {
          key: "workflow_id",
          name: "Workflow",
          type: this.$fieldTypes.SELECT,
          rel: { to: "workflows", key: "id", name: "nome" },
          filterable: true,
          width: "200px"
        },
        {
          key: "sla_label",
          name: "Prazo",
          type: this.$fieldTypes.TEXT,
          width: "200px"
        }
      ];
    },
    customResource() {
      const url = `/v1/ativacaoped/${this.clientId}/business-plans`;
      const resource = this.apiResource(url);
      return {
        ...resource,
        async get(...args) {
          const response = await resource.get(...args);
          return {
            ...response,
            data: response.data.map(({ sla_label, ...rest }) => ({
              ...rest,
              sla_label:
                sla_label?.toLowerCase() === "aprovado"
                  ? "Finalizado"
                  : sla_label
            }))
          };
        }
      };
    },
    taxResource() {
      return this.apiResource(
        `/v1/ativacaoped/${this.clientId}/vpl-parametrizacao`
      );
    },
    exportResource() {
      return this.apiResource(`/v1/ativacaoped/${this.clientId}/exportacao`);
    },
    fields() {
      return [
        {
          key: "none",
          name:
            "Taxa de juros que será aplicada a todos os Business Plans que não tiverem uma taxa própria configurada.",
          type: FieldTypeEnum.SUBTITLE
        },
        {
          key: "taxa_juros_anual",
          name: "Taxa de juros",
          type: FieldTypeEnum.NUMBER,
          rules: [{ rule: "required" }]
        }
      ];
    },
    fieldExport() {
      return {
        key: "filenameExport",
        name: "Nome do arquivo",
        placeholder: "businessplans.xlsx",
        type: this.$fieldTypes.TEXT,
        rules: [{ rule: "required" }, { rule: "max", params: { size: 255 } }]
      };
    },
    responsiblesFields() {
      return [
        {
          key: "business_plans",
          name: "Business Plans",
          type: this.$fieldTypes.AUTOCOMPLETE_MULTIPLE,
          rel: { to: "businessPlans", key: "id", name: "nome" },
          editable: false
        },
        {
          key: "responsaveis",
          name: "Responsaveis",
          type: this.$fieldTypes.AUTOCOMPLETE_MULTIPLE,
          rel: { to: "responsaveis", key: "id", name: "label" },
          rules: [{ rule: "required" }]
        }
      ];
    },
    /**
     * Determina se o usuário logado é um responsável com o nível hierarquico FP&A
     * Testando o label para funcionar para o cliente XP Inc. Não pensado para outros clientes.
     * @type {boolean}
     * @readonly
     */
    hasAdminRole() {
      return (
        this.user.nivel_hierarquico?.tipo_id ===
        TiposResponsaveisEnum.ADMINISTRADOR
      );
    },
  },
  async created() {
    if (this.clientId === 0) {
      this.goToHome();
      return;
    }

    const requests = [this.getStatus(), this.getWorkflows()];
    await Promise.allSettled(requests);
  },
  watch: {
    clientId(value) {
      if (value === 0) {
        this.goToHome();
      }
    }
  },
  methods: {
    closeResponsiblesModal() {
      this.selection = [];
    },
    confirmDeleteMessage(row) {
      return `Deseja excluir o business plan ${row.nome ?? ""}?`;
    },
    async doGetTax() {
      const response = await this.taxResource.get();
      return response.taxa_juros_anual;
    },
    doImport(type = "vpl-consolidado") {
      this.importType = type;
      this.$refs.inputFile?.click();
    },
    async doSaveResponsibles(data, close) {
      try {
        const resource = this.apiResource(
          `/v1/ativacaoped/${this.clientId}/business-plans/responsaveis-acompanhamento`
        );
        const { business_plans, responsaveis } = data;

        await resource.save({
          business_plans,
          responsaveis
        });

        this.$notify({
          group: "geral",
          duration: 5000,
          type: "sucess",
          title: "Responsáveis salvos",
          text: "Os responsáveis pelo acompanhamento foram definidos com sucesso."
        });

        close();
        this.selection = [];
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    },
    async doSaveTax(data, close) {
      try {
        const { taxa_juros_anual } = data;
        await this.taxResource.save({
          taxa_juros_anual: toNumber(taxa_juros_anual)
        });

        if (close) {
          close();
        }
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    },
    async getResponsaveis() {
      const resource = this.apiResource(
        `/v1/ativacaoped/${this.clientId}/responsaveis/selecao`
      );
      const response = await resource.get();
      this.opts.responsaveis = response
        .map(({ nome, email, ...rest }) => ({
          ...rest,
          email,
          nome,
          label: `${nome || email}${nome && email ? ` (${email})` : ""}`
        }))
        .sort(({ label: a }, { label: b }) => a.localeCompare(b));
    },
    async getStatus() {
      const resource = this.apiResource(
        `v1/ativacaoped/${this.clientId}/business-plans/status`
      );
      const response = await resource.get();
      this.opts.statusOpts = response;
    },
    async getWorkflows() {
      const url = `v1/ativacaoped/${this.clientId}/workflow-parametrizacao`;
      const resourceWorkflows = this.apiResource(url);
      const response = await resourceWorkflows.get();
      this.opts.workflows = response.data;
    },
    goToHome() {
      this.notify({
        error:
          "Não é possível acessar o módulo de Ativação P&D sem uma empresa selecionada."
      });
      this.$router.push({ name: "homepage" });
    },
    async onInputChange(event) {
      const [file] = Array.from(event.target.files);
      const MAX_FILE_SIZE = 10; // 10 MegaBytes

      if (file.size > MAX_FILE_SIZE * 1024 * 1024) {
        this.notify({
          error: `O arquivo ${file.name} excedeu o limite de ${MAX_FILE_SIZE}MB e não foi importado.`
        });
        return;
      }

      try {
        this.$root.$emit("addLoadingProcess", "businessplanimport");
        const api = this.api();
        const url = `/v1/ativacaoped/${this.clientId}/business-plans/importar`;
        const formData = new FormData();
        formData.append("arquivo", file);
        formData.append("tipo", this.importType);

        await api.post(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        });

        this.$notify({
          group: "geral",
          duration: 5000,
          type: "sucess",
          title: "Importação concluída",
          text: `O arquivo está na fila para ser analisado e dentro de instantes você receberá uma notificação do sistema.`
        });
        this.$refs.masterDetail?.doLoad();
      } catch (error) {
        this.notify(error);
      } finally {
        this.$root.$emit("removeLoadingProcess", "businessplanimport");
      }

      event.target.value = null; // Importante para poder voltar a anexar mais arquivos
    },
    async openResponsibleModal(row = null) {
      if (!!row && !this.selection.includes(row)) {
        this.selection.push(row);
      }

      if (this.selection.length === 0) {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "info",
          title: `Ação inválida`,
          text: `Nenhum Business Plan foi selecionado.`
        });
        return;
      }

      const hasAdminRole = this.hasAdminRole;
      const isAdmin = AnyAdmin.includes(this.user.tipo_usuario);
      const userId = this.user.id;
      const [editableBPs, nonEditableBPs] = partition(
        this.selection,
        ({ status, usuario_id, workflow_id }) => {
          const inWorkflow = status === BusinessPlanStatus.APROVACAO;
          const isDemandant = userId === usuario_id;
          const isValidEditor = isDemandant || hasAdminRole || isAdmin;
          const editableStep = [
            adjustmentStep.id,
            adjustmentAccountingStep.id
          ].includes(workflow_id);

          return isValidEditor && (!inWorkflow || editableStep);
        }
      );

      if (editableBPs.length === 0 && nonEditableBPs.length > 0) {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: `Ação inválida`,
          text: `Você não tem permissão para alterar estes BPs ou eles estão sobre revisão em alguma etapa do workflow.`
        });
        this.selection = [];
        return;
      } else if (editableBPs.length > 0 && nonEditableBPs.length > 0) {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "info",
          title: `Alguns Business Plans foram removidos da sua seleção`,
          text: `Você não tem permissão para alterar estes BPs ou eles estão sobre revisão em alguma etapa do workflow. Você pode proseguir com os demais Business Plans válidos.`
        });
        this.selection = editableBPs;
      }

      this.opts.businessPlans = editableBPs;
      await this.getResponsaveis();

      this.responsiblesModal.data = {
        business_plans: editableBPs.map(({ id }) => id),
        responsaveis: []
      };
      this.responsiblesModal.opened = true;
    },
    async doExport() {
      try {
        await this.exportResource.save(this.exportData());
        this.openedExport = false;
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "sucess",
          title: "Exportação concluída",
          text: `O arquivo está na fila para ser analisado e dentro de instantes você receberá uma notificação do sistema.`
        });
        this.openedExport = false;
      } catch (error) {
        this.notify(error);
      }
    },
    exportData() {
      return {
        tipo: "business-plans",
        arquivo_nome: this.sanitizeFilename(this.filenameExport),
        filtros: {}
      };
    },
    sanitizeFilename(filename = "") {
      let safeName = filename
        .replace(/[\\\/:*?"<>|]/g, "") // Remove caracteres especiais
        .replace(/\s+/g, "-") // Remove espaços
        .replace(/[\.\s]+$/, "") // Remove pontos além do ponto da extensão
        .normalize("NFD") // Normaliza para separar o código dos acentos do código das letras
        .replace(/[\u0300-\u036f]/g, "") // Remove o código de todos os acentos
        .toLowerCase();

      if (!/\.xlsx$/i.test(safeName)) {
        safeName += ".xlsx";
      }

      return safeName;
    }
  }
};
</script>

<style lang="scss" scoped>
.input-file {
  display: none;
}
</style>
